import { createFeature, createReducer, on } from "@ngrx/store";
import {
    ManagedUser,
    UsersCriteria,
    UsersMutationState,
} from "../models/user.models";
import { UsersActions } from "./users.actions";

export const USERS_PAGE_SIZE = 8;

export interface UsersState {
    users: ManagedUser[];
    total: number;
    invitedTotal: number;
    criteria: UsersCriteria;
    pageIndex: number;
    pageSize: number;
    cursorHistory: string[];
    nextCursor: string | null;
    clientMode: boolean;
    loading: boolean;
    loaded: boolean;
    error: boolean;
    mutation: UsersMutationState | null;
}

const initialState: UsersState = {
    users: [],
    total: 0,
    invitedTotal: 0,
    criteria: { query: "", roles: [], lite: "any", individual: "any" },
    pageIndex: 0,
    pageSize: USERS_PAGE_SIZE,
    cursorHistory: [""],
    nextCursor: null,
    clientMode: false,
    loading: false,
    loaded: false,
    error: false,
    mutation: null,
};

export const usersFeature = createFeature({
    name: "users",
    reducer: createReducer(
        initialState,
        on(
            UsersActions.pageOpened,
            (state): UsersState => ({
                ...state,
                loading: true,
                error: false,
            })
        ),
        on(
            UsersActions.criteriaChanged,
            (state, { criteria }): UsersState => ({
                ...state,
                criteria,
                pageIndex: 0,
                cursorHistory: [""],
                loading: true,
                error: false,
            })
        ),
        on(
            UsersActions.nextPageRequested,
            UsersActions.previousPageRequested,
            (state): UsersState => ({
                ...state,
                loading: true,
                error: false,
            })
        ),
        on(
            UsersActions.clientPageChanged,
            (state, { pageIndex }): UsersState => ({
                ...state,
                pageIndex: Math.max(0, pageIndex),
            })
        ),
        on(
            UsersActions.pageLoaded,
            (
                state,
                {
                    users,
                    total,
                    invitedTotal,
                    nextCursor,
                    requestCursor,
                    direction,
                    clientMode,
                }
            ): UsersState => {
                if (direction === "initial" || clientMode) {
                    return {
                        ...state,
                        users,
                        total,
                        invitedTotal: invitedTotal ?? state.invitedTotal,
                        pageIndex: 0,
                        cursorHistory: [""],
                        nextCursor,
                        clientMode,
                        loading: false,
                        loaded: true,
                        error: false,
                    };
                }

                const pageIndex =
                    direction === "next"
                        ? state.pageIndex + 1
                        : Math.max(0, state.pageIndex - 1);
                const cursorHistory =
                    direction === "next"
                        ? [
                              ...state.cursorHistory.slice(0, pageIndex),
                              requestCursor,
                          ]
                        : state.cursorHistory;
                return {
                    ...state,
                    users,
                    total,
                    invitedTotal: invitedTotal ?? state.invitedTotal,
                    pageIndex,
                    cursorHistory,
                    nextCursor,
                    clientMode,
                    loading: false,
                    loaded: true,
                    error: false,
                };
            }
        ),
        on(
            UsersActions.pageLoadFailed,
            (state): UsersState => ({
                ...state,
                loading: false,
                loaded: true,
                error: true,
            })
        ),
        on(
            UsersActions.addRequested,
            (state, { request }): UsersState => ({
                ...state,
                mutation: {
                    userId: request.email,
                    type: "add",
                    status: "pending",
                },
            })
        ),
        on(
            UsersActions.addSucceeded,
            (state, { email }): UsersState => ({
                ...state,
                invitedTotal: state.invitedTotal + 1,
                mutation: { userId: email, type: "add", status: "success" },
            })
        ),
        on(
            UsersActions.addFailed,
            (state, { email }): UsersState => ({
                ...state,
                mutation: { userId: email, type: "add", status: "error" },
            })
        ),
        on(
            UsersActions.updateRequested,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "save", status: "pending" },
            })
        ),
        on(
            UsersActions.updateSucceeded,
            (state, { userId, update }): UsersState => ({
                ...state,
                users: state.users.map((user) =>
                    user.id === userId
                        ? {
                              ...user,
                              firstName: update.firstName,
                              familyName: update.familyName,
                              fullName: update.fullName,
                              profilePicture: update.profilePicture || null,
                              roles: update.role,
                              tags: update.tags,
                              contactEmail: update.contact.email,
                              phone: update.contact.phone,
                              address: update.contact.address,
                          }
                        : user
                ),
                mutation: { userId, type: "save", status: "success" },
            })
        ),
        on(
            UsersActions.updateFailed,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "save", status: "error" },
            })
        ),
        on(
            UsersActions.deleteRequested,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "delete", status: "pending" },
            })
        ),
        on(
            UsersActions.deleteSucceeded,
            (state, { userId }): UsersState => ({
                ...state,
                users: state.users.filter((user) => user.id !== userId),
                total: Math.max(0, state.total - 1),
                mutation: { userId, type: "delete", status: "success" },
            })
        ),
        on(
            UsersActions.deleteFailed,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "delete", status: "error" },
            })
        ),
        on(
            UsersActions.passwordResetRequested,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "reset", status: "pending" },
            })
        ),
        on(
            UsersActions.passwordResetSucceeded,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "reset", status: "success" },
            })
        ),
        on(
            UsersActions.passwordResetFailed,
            (state, { userId }): UsersState => ({
                ...state,
                mutation: { userId, type: "reset", status: "error" },
            })
        ),
        on(
            UsersActions.mutationStatusCleared,
            (state): UsersState => ({ ...state, mutation: null })
        )
    ),
});
